/* iter55f92 — Voice recorder hook.
 *
 * Extracted from `MobileNotebookPage.jsx` (lines 141-224) to bring that
 * page under 500 lines. Encapsulates the browser MediaRecorder plumbing
 * + Whisper upload path used by the notebook's voice-memo button.
 *
 * The hook keeps the mime-type negotiation, elapsed-time counter, and
 * cleanup-on-unmount that lived in the page — but it never talks to
 * app state directly. Callers pass `uploadBlob(blob)` and receive
 * `{ recording, transcribing, elapsedMs, start, stop }` back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

export function useVoiceRecorder({ onUpload }) {
  const [recording, setRecording]     = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsedMs, setElapsedMs]     = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const recordStartRef   = useRef(0);
  const elapsedTimerRef  = useRef(null);

  const uploadBlob = useCallback(async (blob) => {
    setTranscribing(true);
    try {
      await onUpload(blob);
    } finally { setTranscribing(false); }
  }, [onUpload]);

  const start = useCallback(async () => {
    if (recording || transcribing) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not available in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mimetype the browser + Whisper both understand. We
      // prefer webm/opus (Chrome/Firefox); Safari falls back to mp4.
      const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = preferred.find(
        (t) => typeof window.MediaRecorder !== 'undefined'
                && window.MediaRecorder.isTypeSupported?.(t),
      ) || '';
      const mr = mime
        ? new window.MediaRecorder(stream, { mimeType: mime })
        : new window.MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        window.clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
        const blobType = mr.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        audioChunksRef.current = [];
        if (blob.size < 400) {
          toast.error('Recording was too short.');
          return;
        }
        await uploadBlob(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      recordStartRef.current = Date.now();
      setElapsedMs(0);
      elapsedTimerRef.current = window.setInterval(() => {
        setElapsedMs(Date.now() - recordStartRef.current);
      }, 200);
      setRecording(true);
    } catch (e) {
      toast.error(e?.name === 'NotAllowedError'
        ? 'Microphone permission denied.'
        : 'Could not start recording.');
    }
  }, [recording, transcribing, uploadBlob]);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    setRecording(false);
    try { mr.stop(); } catch { /* already stopped */ }
    mediaRecorderRef.current = null;
  }, []);

  // On unmount: stop any in-flight recording so the mic doesn't stay live.
  useEffect(() => () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
    if (elapsedTimerRef.current) window.clearInterval(elapsedTimerRef.current);
  }, []);

  return { recording, transcribing, elapsedMs, start, stop };
}
